import { debounce } from "./apiUtils.js";
import {
  fetchStations,
  loadingActions,
  stationDataActions,
  viewMode,
  currentViewportBounds,
} from "../../services/station-service.ts";
import { get } from "svelte/store";

const DEBOUNCE_DELAY = 400;
const FETCH_LIMIT = 1000;
const BOUNDS_PRECISION = 3;

const fetchedAreas = new Set();
let activeRequest = null;

function getBoundsKey(bounds) {
  return [
    bounds.west.toFixed(BOUNDS_PRECISION),
    bounds.south.toFixed(BOUNDS_PRECISION),
    bounds.east.toFixed(BOUNDS_PRECISION),
    bounds.north.toFixed(BOUNDS_PRECISION)
  ].join('_');
}

function shouldSkipFetch(bounds, currentMode, isEnabled, hasActiveRequest) {
  return !isEnabled || 
         currentMode !== "viewport" || 
         hasActiveRequest || 
         fetchedAreas.has(getBoundsKey(bounds));
}

export function useViewportFetching() {
  let map = null;
  let isEnabled = true;
  let isDestroyed = false;

  const processViewport = async () => {
    if (!map || isDestroyed) return;

    const bounds = {
      west: map.getBounds().getWest(),
      south: map.getBounds().getSouth(),
      east: map.getBounds().getEast(),
      north: map.getBounds().getNorth()
    };

    currentViewportBounds.set(bounds);

    if (shouldSkipFetch(bounds, get(viewMode), isEnabled, !!activeRequest)) {
      return;
    }

    const boundsKey = getBoundsKey(bounds);
    const bbox = `${bounds.north},${bounds.west},${bounds.south},${bounds.east}`;

    loadingActions.start();

    activeRequest = fetchStations({
      bbox,
      limit: FETCH_LIMIT
    });

    try {
      const result = await activeRequest;

      if (isDestroyed || get(viewMode) !== "viewport") return;

      if (result?.stations?.length > 0) {
        stationDataActions.mergeStations(result.stations);
        fetchedAreas.add(boundsKey);
      }

    } catch (error) {
      console.error("Viewport fetch failed:", error);
    } finally {
      activeRequest = null;
      if (!isDestroyed) {
        loadingActions.stop();
      }
    }
  };

  const debouncedProcessViewport = debounce(processViewport, DEBOUNCE_DELAY);

  function refresh() {
    if (!isDestroyed) {
      processViewport();
    }
  }

  function clearCache() {
    fetchedAreas.clear();
  }

  function setup(mapInstance) {
    map = mapInstance;
    isDestroyed = false;

    map.on("moveend", debouncedProcessViewport);
    processViewport();

    return { refresh, clearCache };
  }

  function cleanup() {
    isDestroyed = true;

    if (debouncedProcessViewport?.cancel) {
      debouncedProcessViewport.cancel();
    }

    activeRequest = null;

    if (map) {
      map.off("moveend", debouncedProcessViewport);
    }
  }

  function enable() {
    isEnabled = true;
  }

  function disable() {
    isEnabled = false;
    activeRequest = null;
  }

  return {
    setup,
    cleanup,
    enable,
    disable
  };
}